import { Box, Flex, Grid, Icon, Text } from "@chakra-ui/react"
import { CheckCircleFilledIcon } from "Icons/CheckCircleFilledIcon"
import { DangerFilledIcon } from "Icons/DangerFilledIcon"
import { InfoFilledIcon } from "Icons/InfoFilledIcon"
import { WarningFilledIcon } from "Icons/WarningFilledIcon"
import { AnimatePresence, motion } from "framer-motion"
import React from "react"
import { Popover } from "./Popover"
import { Tag } from "./Tag"

type Props = {
  skipMessageAnimationOnFirstRender?: boolean
}

// Determines where to place the component in the layout.
// Note that we can't use the component name here since it's minified in production.
const FIELD_SLOTS = [
  "label",
  "description",
  "field",
  "message",
  "formattingHelp",
  "optionalTag",
] as const
type FieldSlot = {
  slot: (typeof FIELD_SLOTS)[number]
}

/**
 * Layout component for form fields. Provides a label, "optional" tag,
 * description, info/warning/error/success messages, and formatting help
 * popover. All of these subcomponents render a `React.ReactNode`.
 *
 * Use `FormField.Root` to wrap everything, and it will place all
 * subcomponents in the correct order.
 *
 * The actual field itself will be a separate component and can be anything.
 * Put it inside `FormField.Field`. If you need to control layout (e.g. a
 * horizontal row of buttons) wrap it in a `<Flex>` inside `FormField.Field`.
 *
 * @param skipMessageAnimationOnFirstRender Prevents the <FormField.Message>
 * from animating in when it first renders. Useful if you have e.g. an "info"
 * status message that will be there on page laod and don't want it to draw
 * attention.
 */
const Root: React.FC<React.PropsWithChildren<Props>> = ({
  skipMessageAnimationOnFirstRender,
  children,
}) => {
  const components: Record<FieldSlot["slot"], React.ReactNode> = {
    label: null,
    description: null,
    field: null,
    message: null,
    formattingHelp: null,
    optionalTag: null,
  }

  React.Children.forEach(children, (child) => {
    if (
      React.isValidElement(child) &&
      typeof child.type !== "string" && // Must be a component not a built-in HTML element
      isValidSlot(child.type)
    ) {
      components[child.type.slot] = child
    }
  })

  return (
    // We use a zero-gap flex container to ensure that the message doesn't
    // suddenly create a gap before it animates in when it's conditionally rendered
    <Flex flexDirection="column" gap={0}>
      <Flex direction="column" gap={2}>
        {(components.label || components.optionalTag) && (
          <Flex justifyContent="space-between">
            {components.label}
            {components.optionalTag}
          </Flex>
        )}
        {components.description}
        {components.field}
      </Flex>
      <AnimatePresence initial={!skipMessageAnimationOnFirstRender}>
        {components.message}
      </AnimatePresence>
      {components.formattingHelp}
    </Flex>
  )
}

// We can't use `for` here because it's a reserved word
const Label: FieldSlot &
  React.FC<React.PropsWithChildren<{ htmlFor?: string }>> = ({
  htmlFor,
  children,
}) => {
  return (
    <Text as="label" textStyle="ds.heading.tertiary" htmlFor={htmlFor}>
      {children}
    </Text>
  )
}

Label.slot = "label"

const Description: FieldSlot & React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  return <Box textStyle="ds.paragraph.secondary">{children}</Box>
}

Description.slot = "description"

const Field: FieldSlot & React.FC<React.PropsWithChildren> = ({ children }) => {
  // We use a box here to prevent surprises from the children being flex
  // children of FormField.Root
  return <Box>{children}</Box>
}

Field.slot = "field"

const MotionBox = motion(Box)

const Message: FieldSlot &
  React.FC<React.PropsWithChildren<{ status?: MessageStatus }>> = ({
  children,
  status,
}) => {
  const messageColor =
    status === undefined ? "ds.text.subtle" : TEXT_COLORS_MAP[status]
  const MessageIcon = () =>
    status === undefined ? null : (
      <Icon as={ICON_MAP[status]} color={ICON_COLORS_MAP[status]} />
    )

  return (
    <MotionBox
      initial={{ height: 0 }}
      animate={{ height: "auto" }}
      exit={{ height: 0 }}
      transition={{ duration: 0.25 }}
      overflow="hidden"
    >
      <Flex gap={1.5} mt={2}>
        <MessageIcon />
        <Text textStyle="ds.paragraph.secondary" color={messageColor}>
          {children}
        </Text>
      </Flex>
    </MotionBox>
  )
}

Message.slot = "message"

const OptionalTag: FieldSlot & React.FC = () => {
  return <Tag label="Optional" />
}

OptionalTag.slot = "optionalTag"

/**
 * @param supported We support different markdown elements depending on the
 * context; setting this will only show the supported ones.
 */
const FormattingHelp: FieldSlot &
  React.FC<{
    supported: ("bold" | "italic" | "lists" | "headings")[]
  }> = ({ supported }) => {
  return (
    <Popover.Root trigger="hover" placement="bottom-start">
      <Popover.Trigger>
        <Text
          as="button"
          textStyle="ds.paragraph.secondary"
          color="ds.link.default"
          textDecoration="underline"
          textDecorationStyle="dotted"
          width="max-content"
          mt={2}
        >
          Formatting help
        </Text>
      </Popover.Trigger>
      <Popover.Content>
        <Flex flexDirection="column" gap={4}>
          <Text as="div" textStyle="ds.heading.primary">
            Formatting help
          </Text>
          <Popover.Divider />
          <Grid
            templateColumns="1fr auto"
            justifyItems="space-between"
            textStyle="ds.paragraph.primary"
            rowGap={3}
            sx={{
              "& > :nth-child(2n)": {
                justifySelf: "end",
              },
            }}
          >
            {supported.includes("headings") && (
              <>
                <Text as="span">Heading</Text>
                <MarkdownExample># X</MarkdownExample>
              </>
            )}
            {supported.includes("italic") && (
              <>
                <Text as="span">Italic</Text>
                <MarkdownExample>_X_</MarkdownExample>
              </>
            )}
            {supported.includes("bold") && (
              <>
                <Text as="span">Bold</Text>
                <MarkdownExample>**X**</MarkdownExample>
              </>
            )}
            {supported.includes("lists") && (
              <>
                <Text as="span">Ordered list</Text>
                <MarkdownExample>1. X</MarkdownExample>
                <Text as="span">Bullet list</Text>
                <MarkdownExample>- X</MarkdownExample>
              </>
            )}
          </Grid>
        </Flex>
      </Popover.Content>
    </Popover.Root>
  )
}

FormattingHelp.slot = "formattingHelp"

const MarkdownExample: React.FC<React.PropsWithChildren> = ({ children }) => {
  return (
    <Text
      as="span"
      bg="ds.background.neutral.resting"
      borderRadius="0.25rem"
      px={1}
      color="ds.text.subtle"
    >
      {children}
    </Text>
  )
}

type MessageStatus = "danger" | "warning" | "success" | "info"

const ICON_MAP: Record<MessageStatus, React.ElementType> = {
  danger: DangerFilledIcon,
  warning: WarningFilledIcon,
  success: CheckCircleFilledIcon,
  info: InfoFilledIcon,
}

const ICON_COLORS_MAP: Record<MessageStatus, string> = {
  danger: "ds.icon.danger",
  warning: "ds.icon.warning",
  success: "ds.icon.success",
  info: "ds.icon.info",
}

const TEXT_COLORS_MAP: Record<MessageStatus, string> = {
  danger: "ds.text.danger",
  warning: "ds.text.warning",
  success: "ds.text.success",
  // TODO update this to "ds.text.info" once it's available in the tokens
  info: "blue.700",
}

export const FormField = {
  Root,
  Label,
  Description,
  Field,
  Message,
  OptionalTag,
  FormattingHelp,
}

const isValidSlot = (childType: object): childType is FieldSlot => {
  return (
    "slot" in childType &&
    FIELD_SLOTS.includes(childType.slot as (typeof FIELD_SLOTS)[number])
  )
}
